import { useMemo } from "react";
import { useFlags } from "@unleash/proxy-client-react";
import { getFeatureFlag, isHiddenFromBrowse } from "@/config/datasources";
import useLegislationFilters from "@/hooks/useLegislationFilters";
import {
  SourceCfg,
  SourceDictionary,
} from "@/models/legislation/LegislationFilter";
import {
  LegislationFilterConfigContext,
  LegislationFilterConfigContextValue,
} from "./hooks/useLegislationFilterConfig";

interface LegislationFilterConfigProviderProps {
  children: React.ReactNode;
}

export const LegislationFilterConfigProvider = ({
  children,
}: LegislationFilterConfigProviderProps) => {
  const flags = useFlags();
  const { useGetFilterConfiguration } = useLegislationFilters();
  const fetchedFilterConfig = useGetFilterConfiguration();

  // Normalize keys: lowercase, strip diacritics, spaces, underscores, and dashes
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\s_-]/g, "");

  // Build a normalized flag map once per flags change
  const normalizedFlags = useMemo(() => {
    const map = new Map<string, boolean>();

    const setEnabled = (name: string, val: unknown) => {
      let enabled = false;
      if (typeof val === "boolean") {
        enabled = val;
      } else if (val && typeof val === "object") {
        const obj = val as {
          enabled?: boolean;
          variant?: {
            enabled?: boolean;
            featureEnabled?: boolean;
            feature_enabled?: boolean;
          };
        };
        enabled = Boolean(
          obj.enabled ??
          obj.variant?.enabled ??
          obj.variant?.featureEnabled ??
          obj.variant?.feature_enabled
        );
      }
      map.set(normalize(name), enabled);
    };

    if (Array.isArray(flags)) {
      for (const f of flags) {
        if (!f || typeof f !== "object" || !("name" in f)) continue;
        const { name, enabled, variant } = f as {
          name: string;
          enabled?: boolean;
          variant?: {
            enabled?: boolean;
            featureEnabled?: boolean;
            feature_enabled?: boolean;
          };
        };
        setEnabled(name, { enabled, variant });
      }
    } else if (flags && typeof flags === "object") {
      Object.entries(flags as Record<string, unknown>).forEach(([name, val]) =>
        setEnabled(name, val)
      );
    }

    return map;
  }, [flags]);

  const isFeatureEnabled = (name: string | null | undefined): boolean => {
    if (!name) return false;
    return normalizedFlags.get(normalize(name)) === true;
  };

  // Process filter config and compute defaultsPlan
  const { filterConfig, defaultsPlan } = useMemo(() => {
    if (!fetchedFilterConfig.isFetched || !fetchedFilterConfig.data) {
      return { filterConfig: undefined, defaultsPlan: null };
    }

    const incoming = fetchedFilterConfig.data as SourceDictionary;

    const filteredEntries = Object.entries(incoming.sources).filter(([key]) => {
      if (isHiddenFromBrowse(key)) return false;

      const flagName = getFeatureFlag(key);
      // If no feature flag exists (null), always show the source
      if (flagName === null) return true;
      // If feature flag exists, check if it's enabled
      return isFeatureEnabled(flagName);
    });

    const cfg: SourceDictionary = {
      ...incoming,
      sources: Object.fromEntries(filteredEntries),
    };

    // Find a source with at least one defaultSelectedOptionIndices set among allowed sources
    const entries = Object.entries(cfg.sources);

    if (!entries.length) {
      return { filterConfig: cfg, defaultsPlan: null };
    }

    const firstWithDefaults = entries.find(([_, s]) =>
      Object.values(s.filters).some(
        (f) =>
          Array.isArray(f.defaultSelectedOptionIndices) &&
          f.defaultSelectedOptionIndices.length > 0
      )
    );

    if (!firstWithDefaults) {
      return { filterConfig: cfg, defaultsPlan: null };
    }

    const [sourceKey, sourceCfg] = firstWithDefaults as [string, SourceCfg];

    // Build child filters from defaultSelectedOptionIndices
    const childFilters: { id: string; value: unknown }[] = [];
    for (const [objectKey, f] of Object.entries(sourceCfg.filters)) {
      if (
        Array.isArray(f.defaultSelectedOptionIndices) &&
        f.defaultSelectedOptionIndices.length > 0
      ) {
        // Build array of option values from indices
        const selectedOptions = f.defaultSelectedOptionIndices
          .map((idx) => {
            if (typeof idx !== "number" || idx < 0) return null;
            return f.options?.[idx];
          })
          .filter((opt): opt is string => opt != null);

        if (selectedOptions.length > 0) {
          // For multi-select filters, use array value; for single-select, use first value
          childFilters.push({
            id: objectKey,
            value: f.type === "Multi" ? selectedOptions : selectedOptions[0],
          });
        }
      }
    }

    return {
      filterConfig: cfg,
      defaultsPlan: { sourceKey, childFilters },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedFilterConfig.isFetched,
    fetchedFilterConfig.data,
    normalizedFlags,
  ]);

  const value: LegislationFilterConfigContextValue = {
    filterConfig,
    defaultsPlan,
    isLoading: fetchedFilterConfig.isPending,
  };

  return (
    <LegislationFilterConfigContext.Provider value={value}>
      {children}
    </LegislationFilterConfigContext.Provider>
  );
};
